/**
 * 차량 사고부위 체크 컴포넌트
 *
 * 성능점검기록부 스타일 - 4면도(상/좌/우/하)에서
 * 부위를 터치하면 상태(교환, 판금, 도장, 긁힘 등)를 마킹
 *
 * Props:
 *   value: { [partKey]: { status, note } } - 초기값
 *   onChange: (data) => void - 변경 콜백
 *   readOnly: boolean - 읽기 전용
 */
import React, { useState, useRef } from 'react';
import bgTop from '../assets/damage-map/diagram-top.png';
import bgLeft from '../assets/damage-map/diagram-left.png';
import bgRight from '../assets/damage-map/diagram-right.png';
import bgBottom from '../assets/damage-map/diagram-bottom.png';
import DamageCheckMapCalibrate, { getCalibrateMode, useCalibrateDrag } from './DamageCheckMapCalibrate';

var OFFICIAL_BG = { top: bgTop, left: bgLeft, right: bgRight, bottom: bgBottom };

// 사고 상태 유형
var DAMAGE_TYPES = [
  { key: 'X_WELD', label: '교환(용접)', short: 'X', color: '#1B5E20', bg: '#E8F5E9', icon: '⊗' },
  { key: 'X_BOLT', label: '교환(볼트)', short: 'X', color: '#E24B4A', bg: '#FCEBEB', icon: '⊕' },
  { key: 'W',      label: '판금',       short: 'W', color: '#0D47A1', bg: '#E3F2FD', icon: '◉' },
  { key: 'P',      label: '도장',       short: 'P', color: '#E65100', bg: '#FFF3E0', icon: '◎' },
  { key: 'A',      label: '긁힘/흠집',  short: 'A', color: '#222',    bg: '#F5F5F5', icon: '●' },
  { key: 'U',      label: '찌그러짐',   short: 'U', color: '#AD1457', bg: '#FCE4EC', icon: '◆' },
  { key: 'C',      label: '부식/녹',    short: 'C', color: '#6A1B9A', bg: '#F3E5F5', icon: '◇' },
  { key: 'T',      label: '깨짐/구멍',  short: 'T', color: '#00838F', bg: '#E0F7FA', icon: '△' },
];

// ── 부위 좌표 (별지 제82호서식 공식 도면 기준) ──
// rect 형 hit-area: { x, y, w, h } in PNG 좌표계
// 좌표 기준 (top 430x365, left 365x385, right 425x385, bottom 420x365)
// 상면 (PNG 430x365) — 캘리브레이션 모드로 사용자 직접 조정
var PARTS_TOP = [
  { key: 'hood', label: '후드', officialNo: 1, rank: '1랭크', x: 178, y: 1, w: 90, h: 86 },
  { key: 'fender_fl', label: '좌앞펜더', officialNo: 2, rank: '1랭크', x: 124, y: 21, w: 28, h: 75 },
  { key: 'fender_fr', label: '우앞펜더', officialNo: 2, rank: '1랭크', x: 286, y: 23, w: 28, h: 75 },
  { key: 'roof', label: '루프', officialNo: 7, rank: '2랭크', x: 184, y: 146, w: 76, h: 88 },
  { key: 'door_fl', label: '좌앞문', officialNo: 3, rank: '1랭크', x: 143, y: 120, w: 24, h: 65 },
  { key: 'door_fr', label: '우앞문', officialNo: 3, rank: '1랭크', x: 270, y: 122, w: 24, h: 65 },
  { key: 'door_rl', label: '좌뒷문', officialNo: 3, rank: '1랭크', x: 144, y: 199, w: 24, h: 65 },
  { key: 'door_rr', label: '우뒷문', officialNo: 3, rank: '1랭크', x: 271, y: 197, w: 24, h: 65 },
  { key: 'trunk_top', label: '트렁크', officialNo: 4, rank: '1랭크', x: 178, y: 282, w: 93, h: 63 },
  { key: 'quarter_l', label: '좌쿼터', officialNo: 6, rank: '2랭크', x: 122, y: 276, w: 28, h: 80 },
  { key: 'quarter_r', label: '우쿼터', officialNo: 6, rank: '2랭크', x: 289, y: 276, w: 28, h: 80 },
];

var PARTS_LEFT = [
  { key: 'fender_fl_s', label: '좌앞펜더', officialNo: 2, rank: '1랭크', x: 174, y: 18, w: 73, h: 85 },
  { key: 'a_pillar_l', label: 'A필러', officialNo: 14, rank: 'B랭크', x: 264, y: 129, w: 66, h: 37, pillarKind: 'A' },
  { key: 'door_fl_s', label: '좌앞문', officialNo: 3, rank: '1랭크', x: 172, y: 135, w: 80, h: 65 },
  { key: 'b_pillar_l', label: 'B필러', officialNo: 14, rank: 'B랭크', x: 267, y: 198, w: 70, h: 27, pillarKind: 'B' },
  { key: 'door_rl_s', label: '좌뒷문', officialNo: 3, rank: '1랭크', x: 177, y: 217, w: 80, h: 60 },
  { key: 'side_sill_l', label: '사이드실', officialNo: 8, rank: '2랭크', x: 133, y: 122, w: 25, h: 130 },
  { key: 'c_pillar_l', label: 'C필러', officialNo: 14, rank: 'B랭크', x: 287, y: 247, w: 46, h: 33, pillarKind: 'C' },
  { key: 'quarter_l_s', label: '좌쿼터', officialNo: 6, rank: '2랭크', x: 180, y: 306, w: 98, h: 57 },
];

var PARTS_RIGHT = [
  { key: 'fender_fr_s', label: '우앞펜더', officialNo: 2, rank: '1랭크', x: 122, y: 16, w: 65, h: 90 },
  { key: 'a_pillar_r', label: 'A필러', officialNo: 14, rank: 'B랭크', x: 43, y: 125, w: 47, h: 54, pillarKind: 'A' },
  { key: 'door_fr_s', label: '우앞문', officialNo: 3, rank: '1랭크', x: 115, y: 134, w: 80, h: 65 },
  { key: 'b_pillar_r', label: 'B필러', officialNo: 14, rank: 'B랭크', x: 35, y: 201, w: 67, h: 22, pillarKind: 'B' },
  { key: 'door_rr_s', label: '우뒷문', officialNo: 3, rank: '1랭크', x: 110, y: 214, w: 80, h: 60 },
  { key: 'side_sill_r', label: '사이드실', officialNo: 8, rank: '2랭크', x: 203, y: 125, w: 15, h: 134 },
  { key: 'c_pillar_r', label: 'C필러', officialNo: 14, rank: 'B랭크', x: 32, y: 232, w: 34, h: 52, pillarKind: 'C' },
  { key: 'quarter_r_s', label: '우쿼터', officialNo: 6, rank: '2랭크', x: 78, y: 296, w: 120, h: 70 },
];

var PARTS_BOTTOM = [
  { key: 'radiator_sup', label: '라디에이터', officialNo: 5, rank: '1랭크', x: 116, y: -7, w: 95, h: 17 },
  { key: 'front_panel', label: '프론트패널', officialNo: 9, rank: 'A랭크', x: 90, y: 18, w: 155, h: 14 },
  { key: 'inside_panel_l', label: '좌인사이드', officialNo: 11, rank: 'A랭크', x: 82, y: 36, w: 21, h: 46 },
  { key: 'inside_panel_r', label: '우인사이드', officialNo: 11, rank: 'A랭크', x: 226, y: 39, w: 24, h: 40 },
  { key: 'sidemember_lf', label: '좌사이드멤버(앞)', officialNo: 12, rank: 'B랭크', x: 120, y: 40, w: 22, h: 62 },
  { key: 'sidemember_rf', label: '우사이드멤버(앞)', officialNo: 12, rank: 'B랭크', x: 184, y: 36, w: 32, h: 66 },
  { key: 'wheelhouse_lf', label: '좌앞휠하우스', officialNo: 13, rank: 'B랭크', x: 76, y: 82, w: 36, h: 22 },
  { key: 'wheelhouse_rf', label: '우앞휠하우스', officialNo: 13, rank: 'B랭크', x: 223, y: 82, w: 31, h: 20 },
  { key: 'cross_member', label: '크로스멤버', officialNo: 10, rank: 'A랭크', x: 90, y: 107, w: 148, h: 13 },
  { key: 'dash_panel', label: '대쉬패널', officialNo: 15, rank: 'C랭크', x: 90, y: 129, w: 147, h: 12 },
  { key: 'floor_front', label: '플로어패널', officialNo: 16, rank: 'C랭크', x: 92, y: 149, w: 144, h: 110 },
  { key: 'trunk_floor', label: '트렁크플로어', officialNo: 17, rank: 'A랭크', x: 127, y: 286, w: 74, h: 42 },
  { key: 'wheelhouse_lr', label: '좌뒷휠하우스', officialNo: 13, rank: 'B랭크', x: 72, y: 263, w: 32, h: 25 },
  { key: 'wheelhouse_rr', label: '우뒷휠하우스', officialNo: 13, rank: 'B랭크', x: 221, y: 264, w: 32, h: 25 },
  { key: 'sidemember_lr', label: '좌사이드멤버(뒤)', officialNo: 12, rank: 'B랭크', x: 74, y: 298, w: 32, h: 35 },
  { key: 'sidemember_rr', label: '우사이드멤버(뒤)', officialNo: 12, rank: 'B랭크', x: 218, y: 296, w: 32, h: 35 },
  { key: 'rear_panel', label: '리어패널', officialNo: 18, rank: 'A랭크', x: 104, y: 337, w: 131, h: 22 },
];

var PARTS = {
  top: PARTS_TOP,
  left: PARTS_LEFT,
  right: PARTS_RIGHT,
  bottom: PARTS_BOTTOM,
};

var VIEW_LABELS = { top: '상면 (위에서)', left: '좌측면', right: '우측면', bottom: '하부 (골격)' };

// 2026-05-10 — getCalibrateMode + drag 로직 + 캘리브레이션 패널 → DamageCheckMapCalibrate.js 분리 (500줄 룰)

export default function DamageCheckMap({ value = {}, onChange, readOnly = false, onDamagePhoto }) {
  var [selectedPart, setSelectedPart] = useState(null);
  var [showLegend, setShowLegend] = useState(false);
  var [hoverPart, setHoverPart] = useState(null);
  var [photoToast, setPhotoToast] = useState(null);
  var damageFileRef = useRef(null);
  var pendingPhotoPartRef = useRef(null);
  // 캘리브레이션 모드 — URL ?calibrate=1
  var [calibrateMode] = useState(getCalibrateMode);
  var [partsState, setPartsState] = useState(function() { return JSON.parse(JSON.stringify(PARTS)); });
  var [dragInfo, setDragInfo] = useState(null);
  // 2026-05-10 입력 모드 4 탭 switcher (Kyle UX 피드백 — 4 view 한 줄 → 1 view 크게)
  var [currentView, setCurrentView] = useState('top');

  var totalDamages = Object.keys(value).length;

  var getDamageType = function(key) {
    var d = value[key];
    if (!d) return null;
    return DAMAGE_TYPES.find(function(t) { return t.key === d.status; }) || null;
  };

  var handlePartClick = function(part) {
    if (readOnly) return;
    setSelectedPart(part);
  };

  var handleSelectDamage = function(damageKey) {
    if (!selectedPart) return;
    var newVal = Object.assign({}, value);
    if (damageKey === 'CLEAR') {
      delete newVal[selectedPart.key];
      if (onChange) onChange(newVal);
      setSelectedPart(null);
    } else {
      newVal[selectedPart.key] = { status: damageKey, note: (value[selectedPart.key] && value[selectedPart.key].note) || '' };
      if (onChange) onChange(newVal);
      // 사진 촬영 가능하면 패널 유지 (사진 버튼 보여주기)
    }
  };

  var handleDamagePhotoClick = function() {
    if (!selectedPart) return;
    pendingPhotoPartRef.current = selectedPart;
    if (damageFileRef.current) damageFileRef.current.click();
  };

  var handleDamageFileChange = function(e) {
    var files = Array.from(e.target.files || []);
    if (!files.length || !pendingPhotoPartRef.current) return;
    var part = pendingPhotoPartRef.current;
    var dmg = value[part.key];
    var dt = dmg ? DAMAGE_TYPES.find(function(t) { return t.key === dmg.status; }) : null;
    var memoText = part.label + (dt ? ' (' + dt.label + ')' : '');
    if (onDamagePhoto) {
      onDamagePhoto(files, memoText);
      setPhotoToast(memoText + ' ' + files.length + '장 추가');
      setTimeout(function() { setPhotoToast(null); }, 2000);
    }
    pendingPhotoPartRef.current = null;
    if (damageFileRef.current) damageFileRef.current.value = '';
  };

  // 차량 외형 SVG (view 인자로 특정 면 렌더)
  var VIEWBOX = {
    top:    { w: 430, h: 365 },
    left:   { w: 365, h: 385 },
    right:  { w: 425, h: 385 },
    bottom: { w: 420, h: 365 },
  };
  // readOnly(리포트) 모드 전용 — 차량 영역에 타이트한 viewBox로 PNG 여백 컷.
  // 4면도 시각적 크기 균형을 맞추기 위해 모두 비슷한 aspect (~0.55-0.62)로 정렬.
  var READONLY_VIEWBOX = {
    top:    { x: 110, y: -5,  w: 220, h: 375 },
    left:   { x: 120, y: 5,   w: 230, h: 375 },
    right:  { x: 18,  y: 5,   w: 215, h: 375 },
    bottom: { x: 60,  y: -15, w: 210, h: 390 },
  };
  // 캘리브레이션 드래그/리사이즈 — DamageCheckMapCalibrate.js 분리 (500줄 룰)
  var startDrag = useCalibrateDrag(VIEWBOX, partsState, setPartsState, dragInfo, setDragInfo, calibrateMode);

  var renderCarSvg = function(view) {
    var v = view;
    var dim = VIEWBOX[v];
    var viewParts = (calibrateMode ? partsState[v] : PARTS[v]) || [];
    var vb = (readOnly && READONLY_VIEWBOX[v]) ? READONLY_VIEWBOX[v] : { x: 0, y: 0, w: dim.w, h: dim.h };

    var clipId = 'vb-clip-' + v;
    return (
      <svg viewBox={vb.x + ' ' + vb.y + ' ' + vb.w + ' ' + vb.h}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', width: '100%', height: '100%', margin: '0 auto', overflow: 'hidden' }}>
        <defs>
          <filter id="partShadow" x="-2%" y="-2%" width="104%" height="104%">
            <feDropShadow dx="0" dy="1" stdDeviation="1" floodOpacity="0.08" />
          </filter>
          <clipPath id={clipId}>
            <rect x={vb.x} y={vb.y} width={vb.w} height={vb.h} />
          </clipPath>
        </defs>

        {/* 공식 도면 배경 (별지 제82호서식) — viewBox 밖 PNG 콘텐츠 클립 */}
        <image href={OFFICIAL_BG[v]} x={0} y={0} width={dim.w} height={dim.h} opacity={0.9} clipPath={'url(#' + clipId + ')'} />

        {/* 부위 영역 (클릭 가능) */}
        {viewParts.map(function(part, idx) {
          var dmg = getDamageType(part.key);
          var isSelected = selectedPart && selectedPart.key === part.key;
          var isHover = hoverPart === part.key;
          // 기본은 완전 투명 (도면을 가리지 않음). hover/선택/마크 시에만 표시
          var fillColor = dmg ? dmg.bg : (isHover ? 'rgba(59,139,212,0.15)' : 'transparent');
          var strokeColor = isSelected ? '#3B8BD4' : dmg ? dmg.color : (isHover ? '#3B8BD4' : 'transparent');
          var strokeW = isSelected ? 2.5 : dmg ? 1.8 : (isHover ? 1.5 : 0);

          // 캘리브레이션 모드: 모든 박스 점선 + 드래그/리사이즈
          if (calibrateMode) {
            return (
              <g key={part.key}>
                <rect x={part.x} y={part.y} width={part.w} height={part.h}
                  fill="rgba(226,75,74,0.15)" stroke="#E24B4A" strokeWidth={1.2} strokeDasharray="3 2"
                  style={{ cursor: 'move' }}
                  onMouseDown={function(e) { startDrag(e, v, idx, 'move'); }} />
                <text x={part.x + 3} y={part.y + 11} fontSize={9} fill="#E24B4A" fontWeight={700} style={{ pointerEvents: 'none' }}>
                  {part.officialNo + ':' + part.label}
                </text>
                {/* 우하단 리사이즈 핸들 */}
                <rect x={part.x + part.w - 6} y={part.y + part.h - 6} width={8} height={8}
                  fill="#E24B4A" stroke="#fff" strokeWidth={1}
                  style={{ cursor: 'nwse-resize' }}
                  onMouseDown={function(e) { startDrag(e, v, idx, 'se'); }} />
                {/* 좌상단 리사이즈 핸들 */}
                <rect x={part.x - 2} y={part.y - 2} width={8} height={8}
                  fill="#E24B4A" stroke="#fff" strokeWidth={1}
                  style={{ cursor: 'nwse-resize' }}
                  onMouseDown={function(e) { startDrag(e, v, idx, 'nw'); }} />
              </g>
            );
          }

          return (
            <g key={part.key}
              onClick={function() { handlePartClick(part); }}
              onMouseEnter={function() { setHoverPart(part.key); }}
              onMouseLeave={function() { setHoverPart(null); }}
              style={{ cursor: readOnly ? 'default' : 'pointer' }}
              filter={dmg ? 'url(#partShadow)' : undefined}>
              <rect x={part.x} y={part.y} width={part.w} height={part.h} rx="3"
                fill={fillColor} stroke={strokeColor} strokeWidth={strokeW} />
              {/* 상태 마크 */}
              {dmg && (
                <text
                  x={part.x + part.w / 2}
                  y={part.y + part.h / 2}
                  textAnchor="middle" dominantBaseline="central"
                  fontSize={14} fill={dmg.color} fontWeight={800}
                  style={{ pointerEvents: 'none' }}>
                  {dmg.icon}{dmg.short}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    );
  };

  return (
    <div>
      {/* 사고부위 사진 input (숨김) */}
      <input ref={damageFileRef} type="file" accept="image/*" multiple onChange={handleDamageFileChange} style={{ display: 'none' }} />
      {/* 사진 추가 토스트 */}
      {photoToast && (
        <div style={{
          position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)',
          background: '#1D9E75', color: '#fff', padding: '10px 24px',
          borderRadius: 10, fontSize: 14, fontWeight: 600, zIndex: 9999,
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        }}>{photoToast}</div>
      )}
      {/* 차량 도면 — 별지 제82호서식 4면도 그리드 */}
      <div style={{
        background: '#fff',
        borderRadius: readOnly ? 0 : 12,
        border: readOnly ? 'none' : '1px solid #eee',
        padding: readOnly ? 0 : '12px 8px',
        marginBottom: 10, position: 'relative',
      }}>
        <div style={{ fontSize: 11, color: '#666', textAlign: 'center', marginBottom: 6, fontWeight: 600 }}>
          자동차성능ㆍ상태점검기록부
          {!readOnly && <span style={{ color: '#3B8BD4', fontWeight: 400 }}> — 부위를 터치하세요</span>}
        </div>
        {readOnly ? (
          // readOnly — 기존 2x2 grid 유지
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            {['left', 'top', 'bottom', 'right'].map(function(v) {
              return (
                <div key={v} style={{
                  display: 'flex', flexDirection: 'column', overflow: 'hidden',
                }}>
                  <div style={{ fontSize: 10, color: '#555', textAlign: 'center', fontWeight: 600, marginBottom: 2, flexShrink: 0 }}>
                    {VIEW_LABELS[v].split(' ')[0]}
                  </div>
                  <div style={{
                    aspectRatio: '1 / 1', width: '100%', overflow: 'hidden',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {renderCarSvg(v)}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // 입력 모드 — 4 탭 switcher + 1 view 크게 (Kyle UX 피드백)
          <div>
            <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
              {['top', 'left', 'right', 'bottom'].map(function(v) {
                var active = currentView === v;
                var viewDamages = (PARTS[v] || []).filter(function(p) { return value[p.key]; }).length;
                return (
                  <button key={v} type="button" onClick={function(){ setCurrentView(v); setSelectedPart(null); }}
                    style={{
                      flex: 1, padding: '8px 4px',
                      background: active ? '#1B3A5C' : '#f1f5f9',
                      color: active ? '#fff' : '#475569',
                      border: 'none', borderRadius: 6,
                      fontSize: 11, fontWeight: 700, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                    }}>
                    <span>{VIEW_LABELS[v].split(' ')[0]}</span>
                    {viewDamages > 0 && (
                      <span style={{
                        fontSize: 9, fontWeight: 800,
                        color: active ? '#fff' : '#E24B4A',
                        background: active ? 'rgba(239,68,68,0.4)' : '#FCEBEB',
                        borderRadius: 8, padding: '1px 5px', minWidth: 14, textAlign: 'center',
                      }}>{viewDamages}</span>
                    )}
                  </button>
                );
              })}
            </div>
            <div style={{
              border: '1px solid #eee', borderRadius: 8, padding: 6, background: '#fafafa',
              aspectRatio: '1 / 1', width: '100%', maxWidth: 360, margin: '0 auto',
              overflow: 'hidden',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {renderCarSvg(currentView)}
            </div>
          </div>
        )}
      </div>

      {/* 캘리브레이션 모드 출력 패널 — DamageCheckMapCalibrate.js 분리 (500줄 룰) */}
      {calibrateMode && <DamageCheckMapCalibrate partsState={partsState} />}

      {/* 상태 선택 패널 */}
      {selectedPart && !readOnly && (
        <div style={{
          background: '#fff', borderRadius: 14, border: '2px solid #3B8BD4',
          padding: 14, marginBottom: 12,
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#222', marginBottom: 10 }}>
            {selectedPart.label} — 상태 선택
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
            {DAMAGE_TYPES.map(function(dt) {
              var isActive = value[selectedPart.key] && value[selectedPart.key].status === dt.key;
              return (
                <button key={dt.key} onClick={function() { handleSelectDamage(dt.key); }}
                  style={{
                    padding: '10px 8px', borderRadius: 8, cursor: 'pointer',
                    border: isActive ? '2px solid ' + dt.color : '1px solid #e0e0da',
                    background: isActive ? dt.bg : '#fff',
                    display: 'flex', alignItems: 'center', gap: 8,
                    fontSize: 13, color: dt.color, fontWeight: isActive ? 700 : 500,
                  }}>
                  <span style={{ fontSize: 16 }}>{dt.icon}</span>
                  {dt.label}
                </button>
              );
            })}
          </div>
          {/* 사고부위 사진 촬영 버튼 */}
          {onDamagePhoto && (
            <button onClick={handleDamagePhotoClick} style={{
              width: '100%', padding: '11px 0', borderRadius: 8, marginTop: 10,
              border: '2px dashed #E24B4A', background: '#FEF2F2',
              cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#E24B4A',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}>
              <span style={{ fontSize: 18 }}>📷</span>
              {selectedPart.label} 사진 촬영
            </button>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button onClick={function() { handleSelectDamage('CLEAR'); }} style={{
              flex: 1, padding: '9px 0', borderRadius: 8,
              border: '1px solid #ddd', background: '#f8f8f5',
              cursor: 'pointer', fontSize: 13, color: '#888',
            }}>상태 초기화</button>
            <button onClick={function() { setSelectedPart(null); }} style={{
              flex: 1, padding: '9px 0', borderRadius: 8,
              border: 'none', background: '#3B8BD4',
              cursor: 'pointer', fontSize: 13, color: '#fff', fontWeight: 600,
            }}>확인</button>
          </div>
        </div>
      )}

      {/* 범례 토글 */}
      <button onClick={function() { setShowLegend(!showLegend); }} style={{
        width: '100%', padding: '8px 0', borderRadius: 8,
        border: '1px solid #e0e0da', background: '#fff',
        cursor: 'pointer', fontSize: 12, color: '#888', marginBottom: 8,
      }}>
        {'표기법 범례 ' + (showLegend ? '▲' : '▼')}
      </button>

      {showLegend && (
        <div style={{
          background: '#f8f8f5', borderRadius: 10, padding: 12, marginBottom: 10,
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4,
        }}>
          {DAMAGE_TYPES.map(function(dt) {
            return (
              <div key={dt.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <span style={{ color: dt.color, fontSize: 14 }}>{dt.icon}</span>
                <span style={{ color: dt.color, fontWeight: 600, width: 14 }}>{dt.short}</span>
                <span style={{ color: '#666' }}>{'= ' + dt.label}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* 마킹된 부위 요약 */}
      {totalDamages > 0 && (
        <div style={{
          background: '#FCEBEB', borderRadius: 10, padding: '10px 14px',
          border: '1px solid #F7C1C1',
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#A32D2D', marginBottom: 6 }}>
            {'사고 부위 ' + totalDamages + '건'}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {Object.entries(value).map(function(entry) {
              var key = entry[0];
              var data = entry[1];
              var dt = DAMAGE_TYPES.find(function(t) { return t.key === data.status; });
              var allParts = [].concat(PARTS.top, PARTS.left, PARTS.right, PARTS.bottom);
              var part = allParts.find(function(p) { return p.key === key; });
              if (!dt || !part) return null;
              return (
                <span key={key} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 8px', borderRadius: 6,
                  background: dt.bg, color: dt.color, fontSize: 11, fontWeight: 600,
                }}>
                  {dt.icon + ' ' + part.label + ' (' + dt.label + ')'}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
